import { useEffect, useState } from "react";
import { Briefcase, DollarSign, Clock, CheckCircle, AlertCircle } from "lucide-react";
import { api } from "../../lib/api";

const PRICE_PER_STUDENT = 3;

export default function Financiero() {
    const [schools, setSchools] = useState([]);
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        async function load() {
            try {
                const [s, p] = await Promise.all([
                    api.get("/ejecutivo/schools"),
                    api.get("/ejecutivo/payments"),
                ]);
                setSchools(s.data || []);
                setPayments(p.data || []);
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        }
        load();
    }, []);

    const totalMensual = schools.reduce((acc, s) => acc + (s.num_students || 0) * PRICE_PER_STUDENT, 0);

    if (loading) return <div className="p-6 text-edu-muted">Cargando…</div>;

    return (
        <div className="p-6 md:p-10 fade-in" data-testid="financiero-page">
            <h1 className="text-3xl font-semibold mb-1 inline-flex items-center gap-2">
                <Briefcase size={26} className="text-edu-navy" /> Panel Financiero
            </h1>
            <p className="text-edu-body mb-6">Ingresos por colegio a $3 por alumno.</p>

            {/* Ingreso total */}
            <div className="bg-edu-gold rounded-2xl p-7 mb-6 text-[#0A0A0A]">
                <DollarSign size={26} />
                <div className="text-xs uppercase tracking-widest font-semibold mt-2">Ingreso mensual total</div>
                <div className="text-5xl font-bold mt-1">${totalMensual.toFixed(2)}</div>
                <div className="text-sm mt-1 opacity-70">{schools.length} colegios activos</div>
            </div>

            {/* Tabla de colegios */}
            <h2 className="text-lg font-semibold mb-3">Colegios</h2>
            <div className="edu-card overflow-hidden mb-8">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-[#F8F9FB] border-b border-edu">
                            <tr className="text-left">
                                <th className="px-4 py-3">Colegio</th>
                                <th className="px-4 py-3">Ubicación</th>
                                <th className="px-4 py-3">Nº Alumnos</th>
                                <th className="px-4 py-3">Precio unitario</th>
                                <th className="px-4 py-3 font-semibold">Total mensual</th>
                                <th className="px-4 py-3">Estado</th>
                            </tr>
                        </thead>
                        <tbody>
                            {schools.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="text-center py-6 text-edu-muted">
                                        Sin colegios registrados.
                                    </td>
                                </tr>
                            )}
                            {schools.map((s) => {
                                const total = (s.num_students || 0) * PRICE_PER_STUDENT;
                                return (
                                    <tr key={s.id} className="border-b border-edu/50">
                                        <td className="px-4 py-3 font-medium">{s.name}</td>
                                        <td className="px-4 py-3 text-edu-muted">{s.state}, {s.country}</td>
                                        <td className="px-4 py-3">{s.num_students || 0}</td>
                                        <td className="px-4 py-3 text-edu-muted">${PRICE_PER_STUDENT}/alumno</td>
                                        <td className="px-4 py-3 font-semibold text-edu-navy">${total.toFixed(2)}</td>
                                        <td className="px-4 py-3">
                                            <span className={`edu-pill ${s.active ? "bg-emerald-100 text-emerald-800" : "bg-gray-100 text-gray-600"}`}>
                                                {s.active ? "Activo" : "Inactivo"}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Historial de pagos */}
            <h2 className="text-lg font-semibold mb-3">Historial de Pagos</h2>
            <div className="edu-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-[#F8F9FB] border-b border-edu">
                            <tr className="text-left">
                                <th className="px-4 py-3">Colegio</th>
                                <th className="px-4 py-3">Fecha</th>
                                <th className="px-4 py-3">Monto</th>
                                <th className="px-4 py-3">Estado</th>
                                <th className="px-4 py-3">Notas</th>
                            </tr>
                        </thead>
                        <tbody>
                            {payments.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="text-center py-6 text-edu-muted">
                                        Sin historial de pagos aún.
                                    </td>
                                </tr>
                            )}
                            {payments.map((p, i) => (
                                <tr key={i} className="border-b border-edu/50">
                                    <td className="px-4 py-3 font-medium">{p.school_name}</td>
                                    <td className="px-4 py-3 text-edu-muted">{p.date}</td>
                                    <td className="px-4 py-3">${p.amount.toFixed(2)}</td>
                                    <td className="px-4 py-3">
                                        <span className={`edu-pill inline-flex items-center gap-1 ${
                                            p.status === "pagado"
                                                ? "bg-emerald-100 text-emerald-800"
                                                : p.status === "pendiente"
                                                ? "bg-yellow-100 text-yellow-800"
                                                : "bg-red-100 text-red-700"
                                        }`}>
                                            {p.status === "pagado" && <CheckCircle size={11} />}
                                            {p.status === "pendiente" && <Clock size={11} />}
                                            {p.status === "vencido" && <AlertCircle size={11} />}
                                            {p.status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-edu-muted">{p.notes || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
